/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file workLocation.js
 * @description
 * WORK-EVENT location pings — feeds the admin "Employee locations" map. The phone's position is recorded ONLY at a
 * work event (clock in / clock out, work-order start, app open), never continuously, and only with the employee's
 * informed consent (a prominent disclosure, see `components/workLocation/WorkLocationDisclosureSheet`).
 *
 * Backend contract (`/api/employee-locations/me/*`, store from the `x-restaurant-id` header):
 *  - `GET  /me/config` -> `{enabled, sources: {clockIn, clockOut, workOrder, appOpen}}`;
 *  - `POST /me/pings`  -> `{points: [{lat, lng, accuracy?, recordedAt, source, refType?, refId?}], platform?}`
 *    (1-50 points) answering `{accepted, skipped, enabled}`. Older servers answer 404/405 -> "feature unavailable",
 *    this module backs off for 24 h.
 *
 * The clock / workday / work-order endpoints take an optional `location` in their own body and the SERVER records the
 * ping for those — callers therefore use {@link attachLocation} and must NOT also call {@link sendPing} for them.
 * {@link sendPing} is for the events that have no endpoint of their own (`app_open`, `break_*`, `manual`).
 *
 * Guarantees: FOREGROUND permission only (never background — the driver route task in `driverTracking.js` is not
 * touched); nothing here ever throws or rejects; a clock action is never delayed by more than the fix timeout
 * (the one-time disclosure waits for the user, by design, before the timer starts).
 *
 * @module helpers/workLocation
 */

/* global setTimeout, clearTimeout */
import AsyncStorage from '@react-native-async-storage/async-storage';
import {Platform} from 'react-native';
import {apiFetch} from './WorkSheetContext';
import {apiErrorStatus} from './apiError';
import {fetchWorkdayState} from './workdayApi';

/** AsyncStorage key: `{restaurantId, config, at}` cached config. */
export const CONFIG_KEY = 'HG_WORK_LOCATION_CONFIG';
/** AsyncStorage key: the offline ping queue. */
export const QUEUE_KEY = 'HG_WORK_LOCATION_QUEUE';
/** AsyncStorage key: `{accepted?: true, declinedAt?: ms}` — own disclosure state (NOT the driver tracking one). */
export const DISCLOSURE_KEY = 'HG_WORK_LOCATION_DISCLOSURE';
/** AsyncStorage key: epoch ms until which the feature is treated as unavailable (404/405). */
export const UNAVAILABLE_KEY = 'HG_WORK_LOCATION_UNAVAILABLE_UNTIL';
/** AsyncStorage key: epoch ms of the last app-open ping attempt. */
export const LAST_APP_OPEN_KEY = 'HG_WORK_LOCATION_LAST_APP_OPEN';

export const CONFIG_TTL_MS = 15 * 60 * 1000;
export const QUEUE_CAP = 200;
export const BATCH_SIZE = 50;
export const UNAVAILABLE_BACKOFF_MS = 24 * 60 * 60 * 1000;
export const DECLINE_BACKOFF_MS = 7 * 24 * 60 * 60 * 1000;
export const APP_OPEN_THROTTLE_MS = 30 * 60 * 1000;
export const DEFAULT_FIX_TIMEOUT_MS = 4000;
export const LAST_KNOWN_MAX_AGE_MS = 5 * 60 * 1000;

/** Config used whenever the feature is off / unknown. */
const DISABLED_CONFIG = Object.freeze({
    enabled: false,
    sources: Object.freeze({clockIn: false, clockOut: false, workOrder: false, appOpen: false}),
});

/** Ping `source` -> `config.sources` flag (sources without a flag only need `enabled`). */
const SOURCE_FLAG = {clock_in: 'clockIn', clock_out: 'clockOut', work_order: 'workOrder', app_open: 'appOpen'};

let memoryConfig = null; // {restaurantId, config, at}
let flushing = false;
let disclosureHandler = null;
let lastAppOpenAt = 0;

/** Test hook: forget every in-memory value. */
export const __resetWorkLocationMemory = () => {
    memoryConfig = null;
    flushing = false;
    disclosureHandler = null;
    lastAppOpenAt = 0;
};

const readJson = async (key, fallback = null) => {
    try {
        const raw = await AsyncStorage.getItem(key);

        return raw ? JSON.parse(raw) : fallback;
    } catch (_error) {
        return fallback;
    }
};

const writeJson = async (key, value) => {
    try {
        await AsyncStorage.setItem(key, JSON.stringify(value));
    } catch (_error) {
        // Non-fatal — a ping is never worth a crash.
    }
};

const removeKeys = async (keys) => {
    try {
        await AsyncStorage.multiRemove(keys);
    } catch (_error) {
        // Non-fatal.
    }
};

/**
 * Active store id (same AsyncStorage source the other API helpers use).
 * @returns {Promise<string>} Store id or `''`.
 */
const getRestaurantId = async () => {
    const restaurant = await readJson('restaurant', null);

    return String(restaurant?._id || restaurant?.id || '');
};

const scopeHeaders = (restaurantId) => (restaurantId ? {'x-restaurant-id': restaurantId} : {});

/**
 * Normalise a server config object.
 * @param {*} raw Response body (optionally `{data}`-wrapped).
 * @returns {{enabled: boolean, sources: {clockIn: boolean, clockOut: boolean, workOrder: boolean, appOpen: boolean}}} Config.
 */
export const normalizeConfig = (raw) => {
    const body = raw && typeof raw === 'object' && raw.data && typeof raw.data === 'object' && !('enabled' in raw) ? raw.data : raw;

    if (!body || typeof body !== 'object') {
        return DISABLED_CONFIG;
    }

    const src = body.sources && typeof body.sources === 'object' ? body.sources : {};

    return {
        enabled: body.enabled === true,
        sources: {
            clockIn: src.clockIn === true,
            clockOut: src.clockOut === true,
            workOrder: src.workOrder === true,
            appOpen: src.appOpen === true,
        },
    };
};

/**
 * Whether the backend was recently found not to know the feature (404/405).
 * @returns {Promise<boolean>} True while backing off.
 */
export const isFeatureUnavailable = async () => {
    const until = Number(await readJson(UNAVAILABLE_KEY, 0)) || 0;

    return until > Date.now();
};

const markUnavailable = async () => {
    await writeJson(UNAVAILABLE_KEY, Date.now() + UNAVAILABLE_BACKOFF_MS);
};

const isUnsupportedStatus = (error) => {
    const status = apiErrorStatus(error);

    return status === 404 || status === 405;
};

/**
 * Drop the cached config so the next read hits the server.
 * @returns {Promise<void>}
 */
export const invalidateWorkLocationConfig = async () => {
    memoryConfig = null;
    await removeKeys([CONFIG_KEY]);
};

/**
 * Work-location config of the active store, cached 15 min in memory + AsyncStorage. Never throws; any failure
 * degrades to "disabled" (or the stale cached value on a transient error).
 *
 * @param {{force?: boolean}} [opts] `force` bypasses the cache.
 * @returns {Promise<{enabled: boolean, sources: object}>} Config.
 */
export const getWorkLocationConfig = async ({force = false} = {}) => {
    try {
        const restaurantId = await getRestaurantId();

        if (!restaurantId || await isFeatureUnavailable()) {
            return DISABLED_CONFIG;
        }

        const fresh = (entry) => entry && entry.restaurantId === restaurantId && Date.now() - Number(entry.at || 0) < CONFIG_TTL_MS;

        if (!force) {
            if (fresh(memoryConfig)) {
                return memoryConfig.config;
            }

            const stored = await readJson(CONFIG_KEY, null);

            if (fresh(stored)) {
                memoryConfig = {restaurantId, config: normalizeConfig(stored.config), at: stored.at};

                return memoryConfig.config;
            }
        }

        try {
            const res = await apiFetch('/employee-locations/me/config', {headers: scopeHeaders(restaurantId), skipDedup: true, skipCache: true});
            const config = normalizeConfig(res);

            memoryConfig = {restaurantId, config, at: Date.now()};
            await writeJson(CONFIG_KEY, memoryConfig);

            return config;
        } catch (error) {
            if (isUnsupportedStatus(error)) {
                await markUnavailable();

                return DISABLED_CONFIG;
            }

            // Transient failure: a stale value of THIS store beats guessing.
            const stale = memoryConfig?.restaurantId === restaurantId ? memoryConfig : await readJson(CONFIG_KEY, null);

            return stale && stale.restaurantId === restaurantId ? normalizeConfig(stale.config) : DISABLED_CONFIG;
        }
    } catch (_error) {
        return DISABLED_CONFIG;
    }
};

/**
 * Register the UI that shows the prominent disclosure (mounted once by `WorkLocationHost`).
 * @param {?function(): Promise<boolean>} handler Resolves true when the user allows.
 * @returns {void}
 */
export const setDisclosureHandler = (handler) => {
    disclosureHandler = typeof handler === 'function' ? handler : null;
};

const loadLocation = () => {
    try {
        // Lazy: tests / headless contexts without the native module just get "no fix".
        return require('expo-location');
    } catch (_error) {
        return null;
    }
};

const toFix = (position) => {
    const c = position?.coords;
    const lat = Number(c?.latitude);
    const lng = Number(c?.longitude);

    if (!c || !Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
        return null;
    }

    const accuracy = Number(c.accuracy);

    return {lat, lng, ...(Number.isFinite(accuracy) && accuracy >= 0 ? {accuracy: Math.round(accuracy)} : {})};
};

/**
 * Make sure the foreground permission is granted, running the one-time disclosure first when needed.
 * @param {object} Location expo-location module.
 * @param {boolean} prompt Whether the user may be asked.
 * @returns {Promise<boolean>} True when a fix may be taken.
 */
const ensurePermission = async (Location, prompt) => {
    const current = await Location.getForegroundPermissionsAsync();
    const state = await readJson(DISCLOSURE_KEY, {});

    // The OS grant may come from another feature (e.g. driver route tracking): work-event
    // capture still needs this user's own acceptance of the work-location disclosure.
    if (current?.granted && state?.accepted) {
        return true;
    }

    if (!prompt || !disclosureHandler || (!current?.granted && current?.canAskAgain === false)) {
        return false;
    }

    if (state?.declinedAt && Date.now() - Number(state.declinedAt) < DECLINE_BACKOFF_MS) {
        return false;
    }

    if (!state?.accepted) {
        const allowed = await disclosureHandler();

        if (!allowed) {
            await writeJson(DISCLOSURE_KEY, {declinedAt: Date.now()});

            return false;
        }

        await writeJson(DISCLOSURE_KEY, {accepted: true, at: Date.now()});
    }

    if (current?.granted) {
        return true;
    }

    const asked = await Location.requestForegroundPermissionsAsync();

    if (asked?.granted) {
        return true;
    }

    // The OS prompt was refused: honour it for a week before asking again.
    await writeJson(DISCLOSURE_KEY, {accepted: true, declinedAt: Date.now()});

    return false;
};

/**
 * A quick location fix for a work event. Returns `null` (never throws) unless the feature is enabled for the store
 * AND foreground permission is granted (or was just granted after the disclosure).
 *
 * @param {object} [opts] Options.
 * @param {number} [opts.timeoutMs=4000] Maximum wait for a fresh fix.
 * @param {boolean} [opts.prompt=true] Allow the disclosure / OS prompt (pass false from headless contexts).
 * @param {?string} [opts.source] Ping source — also requires its `config.sources` flag when it has one.
 * @returns {Promise<?{lat: number, lng: number, accuracy?: number}>} The fix or null.
 */
export const getQuickFix = async ({timeoutMs = DEFAULT_FIX_TIMEOUT_MS, prompt = true, source = null} = {}) => {
    try {
        const config = await getWorkLocationConfig();

        if (!config.enabled) {
            return null;
        }

        const flag = SOURCE_FLAG[source];

        if (flag && !config.sources[flag]) {
            return null;
        }

        const Location = loadLocation();

        if (!Location) {
            return null;
        }

        if (!await ensurePermission(Location, prompt)) {
            return null;
        }

        let timer = null;
        const timeout = new Promise((resolve) => {
            timer = setTimeout(() => resolve(null), Math.max(500, Number(timeoutMs) || DEFAULT_FIX_TIMEOUT_MS));
        });
        const fresh = Promise.resolve()
            .then(() => Location.getCurrentPositionAsync({accuracy: Location.Accuracy?.Balanced}))
            .then(toFix, () => null);

        let fix = null;

        try {
            fix = await Promise.race([fresh, timeout]);
        } finally {
            clearTimeout(timer);
        }

        if (fix) {
            return fix;
        }

        try {
            return toFix(await Location.getLastKnownPositionAsync({maxAge: LAST_KNOWN_MAX_AGE_MS}));
        } catch (_error) {
            return null;
        }
    } catch (_error) {
        return null;
    }
};

/**
 * Whether {@link getQuickFix} will even try for `source` — the store enabled it and the user has not refused the
 * permission (permanently, or within the decline back-off). Never prompts, never throws.
 * Used to choose between "decided by GPS" and the manual office / home-office picker.
 *
 * @param {?string} [source] Ping source (e.g. `clock_in`).
 * @returns {Promise<boolean>} True when a fix will be attempted.
 */
export const canAttemptWorkFix = async (source = null) => {
    try {
        const config = await getWorkLocationConfig();
        const flag = SOURCE_FLAG[source];

        if (!config.enabled || (flag && !config.sources[flag])) {
            return false;
        }

        const Location = loadLocation();

        if (!Location) {
            return false;
        }

        const current = await Location.getForegroundPermissionsAsync();

        if (current?.granted) {
            return true;
        }

        const state = await readJson(DISCLOSURE_KEY, {});
        const recentlyDeclined = state?.declinedAt && Date.now() - Number(state.declinedAt) < DECLINE_BACKOFF_MS;

        return current?.canAskAgain !== false && !recentlyDeclined;
    } catch (_error) {
        return false;
    }
};

/**
 * Add `location` to a request body when a fix is available; otherwise return the body untouched. Never throws.
 * @param {object} body Request body.
 * @param {object} [opts] {@link getQuickFix} options.
 * @returns {Promise<object>} Body, with `location: {lat, lng, accuracy?}` when a fix was taken.
 */
export const attachLocation = async (body = {}, opts = {}) => {
    const fix = await getQuickFix(opts);

    return fix ? {...body, location: fix} : body;
};

/**
 * Read the persisted ping queue.
 * @returns {Promise<object[]>} Queued points (oldest first).
 */
export const getQueue = async () => {
    const queue = await readJson(QUEUE_KEY, []);

    return Array.isArray(queue) ? queue : [];
};

const enqueue = async (point) => {
    const queue = await getQueue();

    queue.push(point);
    await writeJson(QUEUE_KEY, queue.slice(-QUEUE_CAP));
};

/**
 * Upload the queue in batches of 50. Re-entrancy safe, never throws.
 * - 200 `enabled:false` -> queue dropped and the config cache refreshed on next read;
 * - 404/405 -> feature unavailable for 24 h, queue dropped;
 * - 400/422 -> the poisoned batch is dropped; network / 5xx -> kept for the next foreground.
 * @returns {Promise<{sent: number, remaining: number}>} Outcome.
 */
export const flushQueue = async () => {
    if (flushing) {
        return {sent: 0, remaining: (await getQueue()).length};
    }

    flushing = true;
    let sent = 0;

    try {
        if (await isFeatureUnavailable()) {
            return {sent, remaining: (await getQueue()).length};
        }

        const restaurantId = await getRestaurantId();
        let queue = await getQueue();

        while (queue.length && restaurantId) {
            const batch = queue.slice(0, BATCH_SIZE);

            try {
                const res = await apiFetch('/employee-locations/me/pings', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json', ...scopeHeaders(restaurantId)},
                    body: JSON.stringify({points: batch, platform: Platform.OS}),
                });
                const body = res && typeof res === 'object' && res.data && typeof res.data === 'object' && !('enabled' in res) ? res.data : res;

                sent += batch.length;

                if (body && body.enabled === false) {
                    queue = [];
                    await invalidateWorkLocationConfig();
                    break;
                }

                queue = queue.slice(batch.length);
            } catch (error) {
                const status = apiErrorStatus(error);

                if (status === 404 || status === 405) {
                    await markUnavailable();
                    queue = [];
                } else if (status === 400 || status === 422) {
                    queue = queue.slice(batch.length);
                    continue;
                }

                break;
            } finally {
                await writeJson(QUEUE_KEY, queue);
            }
        }

        return {sent, remaining: queue.length};
    } catch (_error) {
        return {sent, remaining: 0};
    } finally {
        flushing = false;
    }
};

/** Sources the server records with the clock / workday request itself (never gated client-side). */
const CLOCK_SOURCES = ['clock_in', 'clock_out'];

/**
 * Whether the caller has a running workday (working time). Never throws.
 * @returns {Promise<boolean>} True when running, or when the backend has no workday feature.
 */
export const isWorkingNow = async () => {
    try {
        const res = await fetchWorkdayState();

        return res.ok ? !!res.state?.running : !!res.unsupported;
    } catch (_error) {
        return false;
    }
};

/**
 * Record a work-event ping: take a quick fix, queue it (offline-safe) and flush. For events WITHOUT an endpoint of
 * their own — clock / workday / work-order starts attach `location` to their request instead ({@link attachLocation}).
 *
 * @param {string} source `clock_in|clock_out|work_order|app_open|break_start|break_end|manual`.
 * @param {{refType?: string, refId?: string}} [ref] Optional reference.
 * @returns {Promise<boolean>} True when a point was queued.
 */
export const sendPing = async (source, {refType, refId} = {}) => {
    try {
        // GDPR: location is recorded only during working time - no running workday, no point (the clock events
        // are recorded by the server itself). Workday feature missing on the backend: not gated. Unknown state: skip.
        if (!CLOCK_SOURCES.includes(source) && !(await isWorkingNow())) {
            return false;
        }

        const fix = await getQuickFix({source});

        if (!fix) {
            return false;
        }

        await enqueue({
            ...fix,
            recordedAt: new Date().toISOString(),
            source,
            ...(refType ? {refType: String(refType)} : {}),
            ...(refId ? {refId: String(refId)} : {}),
        });
        await flushQueue();

        return true;
    } catch (_error) {
        return false;
    }
};

/**
 * `app_open` ping, throttled to once per 30 min (persisted, so cold starts count). Needs the feature enabled with
 * `sources.appOpen`. Never throws.
 * @returns {Promise<boolean>} True when a point was queued.
 */
export const sendAppOpenPing = async () => {
    try {
        const config = await getWorkLocationConfig();

        if (!config.enabled || !config.sources.appOpen) {
            return false;
        }

        const now = Date.now();
        const last = Math.max(lastAppOpenAt, Number(await readJson(LAST_APP_OPEN_KEY, 0)) || 0);

        if (now - last < APP_OPEN_THROTTLE_MS) {
            return false;
        }

        // Stamp BEFORE the fix: a denied permission / slow GPS must not be retried on every foreground.
        lastAppOpenAt = now;
        await writeJson(LAST_APP_OPEN_KEY, now);

        return await sendPing('app_open');
    } catch (_error) {
        return false;
    }
};

/**
 * Logout cleanup: forget the queue, config cache, throttles and the disclosure answer, so the
 * next user signing in on a shared device is informed again before any capture.
 * @returns {Promise<void>}
 */
export const clearWorkLocationData = async () => {
    memoryConfig = null;
    lastAppOpenAt = 0;
    await removeKeys([CONFIG_KEY, QUEUE_KEY, LAST_APP_OPEN_KEY, UNAVAILABLE_KEY, DISCLOSURE_KEY]);
};

export default {
    getWorkLocationConfig,
    getQuickFix,
    attachLocation,
    sendPing,
    isWorkingNow,
    sendAppOpenPing,
    flushQueue,
    clearWorkLocationData,
};
